import { hot } from 'react-hot-loader/root';
import React, { useState } from 'react';
import template from 'lodash/template';
import PropTypes from 'prop-types';

import PageWrapper from '../../design-system/PageWrapper';
import PageSection from '../../design-system/PageSection';
import IconPageSection from '../../design-system/IconPageSection';
import { Grid, GridElement } from '../../design-system/Grid';
import DashboardImageCard from '../../design-system/cards/DashboardImageCard';
import DashboardCareersCard from '../../components/dashboard/DashboardCareersCard';
import AttributesCard from '../../components/AttributesCard';
import AccountSubNav from '../../components/AccountSubNav';
import DashboardSpyRoleCard from '../../components/dashboard/DashboardSpyRoleCard';
import { visitGroupType } from '../../components/group-visit/GroupVisits';

import careersType from '../../proptypes/careers';
import phrasesType from '../../proptypes/phrases';
import formType from '../../proptypes/forms';
import rankingType, { additionalRankingType } from '../../proptypes/ranking';
import comparisonOptionsType from '../../proptypes/comparison-options';
import attributesConfigType from '../../proptypes/attributes-config';
import { archetypeContentType } from '../../proptypes/archetype-content';
import Container from '../../design-system/Containers';
import DossierPromo from '../../components/DossierPromo';
import WelcomeBannerSection from './WelcomeBannerSection';
import GroupVisitSection from '../../components/group-visit/GroupVisitSection';
import TheScienceSection from './TheScienceSection';
import PremiumUnlockModal from './PremiumUnlockModal';
import PremiumPaymentSuccessModal from './PremiumPaymentSuccessModal';
import DownloadingModal from './DownloadingModal';
import DossierAd from '../../components/DossierAd';

const selectDashboardPhrases = (phrases, locked) => {
  if (!locked) return phrases;

  return {
    ...phrases,
    attributesCardButtonLabel: phrases.unlockProfileButtonLabel,
    scoresCardButtonLabel: phrases.unlockProfileButtonLabel,
    careersCardButtonLabel: phrases.unlockProfileButtonLabel,
    spyRoleCardButtonLabel: phrases.unlockProfileButtonLabel,
    dossierPromoDescription: phrases.dossierPromoDescriptionLocked,
    dossierPromoButtonLabel: phrases.dossierPromoButtonLabelLocked,
  };
};

const selectCheckoutPath = (path, profiles) => {
  const base = window.location.origin;
  const url = new URL(path, base);
  const profileIds = profiles.map(({ id }) => id);

  const searchParams = new URLSearchParams(url.search);
  profileIds.forEach(id => searchParams.append('profiles[]', id));
  url.search = searchParams.toString();

  return `${url.pathname}${url.search}`;
};

const selectPremiumModalCopy = (currentProfile, profiles, phrases) => {
  const profile = profiles[0];

  if (currentProfile.id === profile.id) return phrases;

  return {
    ...phrases,
    lockedTitle: template(phrases.lockedCompanionTitle)({
      full_name: profile.fullName,
    }),
  };
};

const DashboardPage = ({
  attributesCard,
  scoresCard,
  careersCard,
  spyRoleCard,
  profileFullName,
  profileId,
  theSciencePath,
  group,
  phrases,
  premiumProfile,
  premiumProfilesEnabled,
  printedDossierEnabled,
  checkoutSessionId,
  dossierPath,
  checkoutPath: baseCheckoutPath,
  unlockAllProfilesPath,
  profileInviteForm,
  printedDossierCheckoutPath,
  printedDossierPreviewLink,
  printedDossierPriceCents,
}) => {
  const currentProfile = { fullName: profileFullName, id: profileId };
  const locked = !premiumProfile && premiumProfilesEnabled;

  const [unlockPremiumModalVisible, setUnlockPremiumModalVisible] = useState(
    false,
  );
  const [selectedProfiles, setSelectedProfiles] = useState([currentProfile]);
  const hideUnlockPremiumModal = () => setUnlockPremiumModalVisible(false);
  const showUnlockPremiumModal = (profiles) => {
    setSelectedProfiles(profiles);
    setUnlockPremiumModalVisible(true);
  };

  const showCurrentProfileUnlockPremiumModal = () => {
    showUnlockPremiumModal([currentProfile]);
  };

  const showCompanionProfileUnlockPremiumModal = (profiles) => {
    showUnlockPremiumModal(profiles);
  };

  const premiumModalCopy = selectPremiumModalCopy(
    currentProfile,
    selectedProfiles,
    phrases.premiumProfileModal,
  );

  const [paymentSuccessModalVisible, setPaymentSuccessModalVisible] = useState(
    !!checkoutSessionId,
  );

  const checkoutPath = selectCheckoutPath(baseCheckoutPath, selectedProfiles);

  const [downloadingModalVisible, setDownloadingModalVisible] = useState(false);
  const showDownloadingModal = () => setDownloadingModalVisible(true);

  const {
    attributesCardTitle,
    attributesCardButtonLabel,
    scoresCardTitle,
    scoresCardButtonLabel,
    careersCardTitle,
    careersCardButtonLabel,
    spyRoleCardTitle,
    spyRoleCardButtonLabel,
    headerBannerSalutation,
    theScienceTitle,
    theScienceDescription,
    theScienceLinkLabel,
    visitsTitle,
    dossierPromoTitle,
    dossierPromoDescription,
    dossierPromoButtonLabel,
    printedDossierTitle,
    printedDossierDescription,
    printedDossierButtonLabel,
    printedDossierPreviewButtonLabel,
  } = selectDashboardPhrases(phrases.profilesDashboard, locked);

  return (
    <PageWrapper tallNav bgDark>
      <AccountSubNav />
      <WelcomeBannerSection
        profileFullName={profileFullName}
        salutation={headerBannerSalutation}
      />
      {premiumProfilesEnabled && (
        <PageSection>
          <Container pxXs>
            <DossierPromo
              onButtonLockedClick={showCurrentProfileUnlockPremiumModal}
              onButtonUnlockedClick={showDownloadingModal}
              locked={locked}
              title={dossierPromoTitle}
              description={dossierPromoDescription}
              buttonText={dossierPromoButtonLabel}
              buttonLink={dossierPath}
            />
          </Container>
        </PageSection>
      )}
      <PageSection>
        <Grid smVertical mdHorizontal equalWidth>
          <GridElement pxXs>
            <DashboardImageCard
              onButtonClick={showCurrentProfileUnlockPremiumModal}
              locked={locked}
              title={scoresCardTitle}
              image={{
                src: scoresCard.image,
                link: scoresCard.linkUrl,
                alt: scoresCardButtonLabel,
              }}
              footer={{
                buttonLink: scoresCard.linkUrl,
                buttonText: scoresCardButtonLabel,
              }}
            />
          </GridElement>
          <GridElement pxXs>
            <DashboardSpyRoleCard
              onButtonClick={showCurrentProfileUnlockPremiumModal}
              locked={locked}
              title={spyRoleCardTitle}
              spyRoleName={
                locked
                  ? phrases.profilesDashboard.spyRoleNameLocked
                  : spyRoleCard.archetypeContent.name
              }
              spyRoleImage={spyRoleCard.archetypeContent.roleWallpaperImage}
              imageLink={spyRoleCard.linkUrl}
              footer={{
                buttonLink: spyRoleCard.linkUrl,
                buttonText: spyRoleCardButtonLabel,
              }}
            />
          </GridElement>
        </Grid>
        <Grid smVertical lgHorizontal mdEqualWidth>
          <GridElement pxXs>
            <AttributesCard
              onButtonClick={showCurrentProfileUnlockPremiumModal}
              locked={locked}
              {...{
                ...attributesCard,
                title: attributesCardTitle,
                footer: {
                  buttonLink: attributesCard.buttonPath,
                  buttonText: attributesCardButtonLabel,
                },
                phrases: phrases.attributes,
              }}
            />
          </GridElement>
          <GridElement pxXs>
            <DashboardCareersCard
              onButtonClick={showCurrentProfileUnlockPremiumModal}
              locked={locked}
              {...careersCard}
              title={careersCardTitle}
              footer={{
                buttonLink: careersCard.linkUrl,
                buttonText: careersCardButtonLabel,
              }}
            />
          </GridElement>
        </Grid>
      </PageSection>
      <GroupVisitSection
        group={group}
        phrases={{
          groupVisitor: phrases.groupVisitor,
          profileInvite: phrases.profileInvite,
        }}
        title={visitsTitle}
        onUnlockProfileClick={showCompanionProfileUnlockPremiumModal}
        onDownloadDossierClick={showDownloadingModal}
        showPremiumOptions={premiumProfilesEnabled}
        unlockAllProfilesPath={unlockAllProfilesPath}
        profileInviteForm={profileInviteForm}
      />
      <TheScienceSection
        title={theScienceTitle}
        description={theScienceDescription}
        linkText={theScienceLinkLabel}
        linkPath={theSciencePath}
      />
      {printedDossierEnabled && (
        <IconPageSection>
          <Container dossier>
            <DossierAd
              previewLink={printedDossierPreviewLink}
              buyBtnPath={printedDossierCheckoutPath}
              phrases={{
                title: printedDossierTitle,
                pitch: printedDossierDescription,
                buyButtonLabel: printedDossierButtonLabel,
                previewButtonLabel: printedDossierPreviewButtonLabel,
                notInterested: '',
                online: '',
                modalTitle: phrases.downloadingModal.title,
                modalDescription: phrases.downloadingModal.description,
                modalButtonLabel: phrases.downloadingModal.buttonLabel,
              }}
              priceCents={printedDossierPriceCents}
            />
          </Container>
        </IconPageSection>
      )}
      {premiumProfilesEnabled && (
        <PremiumUnlockModal
          visible={unlockPremiumModalVisible}
          buttonLink={checkoutPath}
          phrases={premiumModalCopy}
          onCloseClick={hideUnlockPremiumModal}
        />
      )}
      {paymentSuccessModalVisible && (
        <PremiumPaymentSuccessModal
          visible
          onCloseClick={() => setPaymentSuccessModalVisible(false)}
          phrases={phrases.premiumPaymentSuccessModal}
          modalDismissedCookieValue={checkoutSessionId}
        />
      )}
      {downloadingModalVisible && (
        <DownloadingModal
          visible
          onCloseClick={() => setDownloadingModalVisible(false)}
          phrases={phrases.downloadingModal}
        />
      )}
    </PageWrapper>
  );
};

DashboardPage.defaultProps = {
  group: null,
  unlockAllProfilesPath: null,
  checkoutSessionId: null,
  printedDossierCheckoutPath: null,
};

DashboardPage.propTypes = {
  group: PropTypes.exact({
    members: visitGroupType,
    visitDate: PropTypes.string,
  }),
  phrases: phrasesType.isRequired,
  careersCard: PropTypes.exact({
    linkUrl: PropTypes.string.isRequired,
    careers: careersType.isRequired,
  }).isRequired,
  attributesCard: PropTypes.exact({
    buttonPath: PropTypes.string,
    ranking: rankingType,
    additionalRanking: additionalRankingType,
    averageRanking: rankingType,
    config: attributesConfigType,
    comparisonOptions: comparisonOptionsType,
  }).isRequired,
  profileFullName: PropTypes.string.isRequired,
  profileId: PropTypes.string.isRequired,
  scoresCard: PropTypes.exact({
    linkUrl: PropTypes.string,
    image: PropTypes.string,
  }).isRequired,
  spyRoleCard: PropTypes.exact({
    linkUrl: PropTypes.string.isRequired,
    archetypeContent: archetypeContentType.isRequired,
  }).isRequired,
  theSciencePath: PropTypes.string.isRequired,
  premiumProfile: PropTypes.bool.isRequired,
  premiumProfilesEnabled: PropTypes.bool.isRequired,
  printedDossierEnabled: PropTypes.bool.isRequired,
  dossierPath: PropTypes.string.isRequired,
  checkoutPath: PropTypes.string.isRequired,
  unlockAllProfilesPath: PropTypes.string,
  printedDossierCheckoutPath: PropTypes.string,
  printedDossierPreviewLink: PropTypes.string.isRequired,
  printedDossierPriceCents: PropTypes.number.isRequired,
  checkoutSessionId: PropTypes.string,
  profileInviteForm: formType.isRequired,
};

export default hot(DashboardPage);
